import React, { useEffect, useState } from 'react';
import { format } from 'date-fns';
import { ro } from 'date-fns/locale';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Users,
  Stethoscope,
  CalendarDays,
  AlertTriangle,
  Download,
  TrendingUp,
  TrendingDown,
  Minus,
  Filter,
} from 'lucide-react';
import { getStatistics } from '../services/api';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import {
  PieChart, Pie, Cell, Tooltip, ResponsiveContainer,
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
} from 'recharts';

const PERIOD_OPTIONS = [
  { value: 'today', label: 'Azi' },
  { value: 'week', label: 'Saptamana' },
  { value: 'month', label: 'Luna' },
  { value: 'year', label: 'Anul' },
];

const DIAG_COLORS = ['#10B981', '#0EA5E9', '#F59E0B', '#8B5CF6', '#EC4899'];
const MED_COLORS = ['#3B82F6', '#0EA5E9', '#6366F1', '#8B5CF6', '#A855F7'];
const DEMO_COLORS = ['#60A5FA', '#3B82F6', '#1D4ED8'];

const STATUS_MAP = {
  scheduled: { label: 'Programat', cls: 'bg-blue-100 text-blue-700' },
  completed: { label: 'Finalizat', cls: 'bg-emerald-100 text-emerald-700' },
  cancelled: { label: 'Anulat', cls: 'bg-red-100 text-red-700' },
};

const tooltipStyle = {
  backgroundColor: '#fff',
  border: '1px solid #E2E8F0',
  borderRadius: '10px',
  fontSize: '12px',
  boxShadow: '0 4px 12px rgba(0,0,0,.08)',
};

// ── KPI Card ──
const KPICard = ({ icon: Icon, title, value, prev, color, isBadMetric }) => {
  const diff = prev != null && prev > 0 ? ((value - prev) / prev) * 100 : null;
  const isPositive = diff != null && diff > 0;
  const isNeutral = diff == null || diff === 0;
  // For "bad" metrics like high risk, positive change is bad
  const trendColor = isNeutral
    ? 'text-slate-400'
    : isBadMetric
      ? isPositive ? 'text-red-500' : 'text-emerald-500'
      : isPositive ? 'text-emerald-500' : 'text-amber-500';
  const TrendIcon = isNeutral ? Minus : isPositive ? TrendingUp : TrendingDown;

  return (
    <Card className="relative overflow-hidden" data-testid={`kpi-${title.toLowerCase().replace(/\s/g, '-')}`}>
      <CardContent className="p-5">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">{title}</p>
            <p className={cn('text-3xl font-bold mt-1', color)}>{value}</p>
          </div>
          <div className={cn('w-11 h-11 rounded-xl flex items-center justify-center', `${color.replace('text-', 'bg-').replace('700', '100')}`)}>
            <Icon className={cn('w-5 h-5', color)} />
          </div>
        </div>
        {diff != null && (
          <div className={cn('flex items-center gap-1 mt-3 text-xs font-medium', trendColor)}>
            <TrendIcon className="w-3.5 h-3.5" />
            <span>{Math.abs(diff).toFixed(1)}%</span>
            <span className="text-slate-400 font-normal ml-1">vs. perioada ant.</span>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// ── Section Card ──
const SectionCard = ({ title, subtitle, children, className }) => (
  <Card className={cn('flex flex-col', className)}>
    <div className="px-5 pt-4 pb-2">
      <h3 className="text-sm font-semibold text-slate-800">{title}</h3>
      {subtitle && <p className="text-xs text-slate-400 mt-0.5">{subtitle}</p>}
    </div>
    <CardContent className="flex-1 px-5 pb-5 pt-0 min-h-0">{children}</CardContent>
  </Card>
);

// ── Custom Donut Label ──
const DonutCenterLabel = ({ viewBox, total }) => {
  const { cx, cy } = viewBox;
  return (
    <text x={cx} y={cy} textAnchor="middle" dominantBaseline="middle">
      <tspan x={cx} dy="-6" className="fill-slate-800 text-2xl font-bold">{total}</tspan>
      <tspan x={cx} dy="18" className="fill-slate-400 text-[10px]">pacienti</tspan>
    </text>
  );
};

// ───────────── MAIN COMPONENT ─────────────
const ReportsPage = () => {
  const [data, setData] = useState(null);
  const [period, setPeriod] = useState('month');
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await getStatistics(period);
      setData(res.data);
    } catch (e) {
      toast.error('Eroare la incarcarea statisticilor');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [period]);

  // Formatted trend labels — removed (trend chart removed)

  // Diagnoses total for percentage calculation
  const diagTotal = data?.top_diagnoses?.reduce((s, d) => s + d.count, 0) || 0;

  // CSV export
  const exportCSV = () => {
    if (!data) return;
    const rows = [
      ['Raport DiabetoCare', format(new Date(), 'dd/MM/yyyy HH:mm')],
      [],
      ['KPI', 'Valoare'],
      ['Total Pacienti', data.kpi.total_patients],
      ['Consultatii (Perioada)', data.kpi.consultations_count],
      ['Programari Azi', data.kpi.today_appointments],
      ['Risc Ridicat (HbA1c>9%)', data.kpi.high_risk_count],
      [],
      ['Distributie HbA1c', 'Numar'],
      ...data.hba1c_distribution.map(h => [h.name, h.value]),
      [],
      ['Clase Medicamente', 'Pacienti'],
      ...data.medication_classes.map(m => [m.name, m.count]),
      [],
      ['Top Diagnostice', 'Cazuri'],
      ...data.top_diagnoses.map(d => [d.name, d.count]),
    ];
    const csv = rows.map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `raport_diabetocare_${format(new Date(), 'yyyy-MM-dd')}.csv`;
    a.click();
    toast.success('Raport exportat');
  };

  const hba1cTotal = data?.hba1c_distribution?.reduce((s, h) => s + h.value, 0) || 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="reports-loading">
        <div className="w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!data) return null;

  return (
    <div className="space-y-6 pb-8" data-testid="reports-page">
      {/* ── HEADER ── */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Rapoarte & Statistici</h1>
          <p className="text-sm text-slate-500">{format(new Date(), "EEEE, d MMMM yyyy", { locale: ro })}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* Period Filter */}
          <div className="inline-flex bg-slate-100 rounded-lg p-0.5" data-testid="period-filter">
            {PERIOD_OPTIONS.map(opt => (
              <button
                key={opt.value}
                onClick={() => setPeriod(opt.value)}
                className={cn(
                  'px-3 py-1.5 rounded-md text-xs font-medium transition-all',
                  period === opt.value
                    ? 'bg-white text-emerald-700 shadow-sm'
                    : 'text-slate-500 hover:text-slate-700',
                )}
                data-testid={`period-${opt.value}`}
              >
                {opt.label}
              </button>
            ))}
          </div>
          <Button size="sm" variant="outline" onClick={exportCSV} data-testid="export-btn">
            <Download className="w-4 h-4 mr-1.5" />
            Export
          </Button>
        </div>
      </div>

      {/* ── KPI ROW ── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KPICard icon={Users} title="Total Pacienti" value={data.kpi.total_patients} prev={data.kpi.patients_prev_count} color="text-blue-700" />
        <KPICard icon={Stethoscope} title="Consultatii" value={data.kpi.consultations_count} prev={data.kpi.consultations_prev} color="text-emerald-700" />
        <KPICard icon={CalendarDays} title="Agenda Azi" value={data.kpi.today_appointments} color="text-amber-700" />
        <KPICard icon={AlertTriangle} title="Alerta Clinica" value={data.kpi.high_risk_count} color="text-red-700" isBadMetric />
      </div>

      {/* ── ROW 2: Top Diagnostice + HbA1c (2 columns) ── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Top 5 Diagnoses - Pie with percentages */}
        <SectionCard title="Top 5 Diagnostice" subtitle="Cele mai frecvente diagnostice">
          {data.top_diagnoses.length === 0 ? (
            <EmptyState text="Nu exista diagnostice" />
          ) : (
            <div className="flex items-center h-64 gap-4">
              <div className="w-1/2 h-full">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={data.top_diagnoses}
                      cx="50%" cy="50%"
                      outerRadius="80%"
                      paddingAngle={2}
                      dataKey="count"
                      strokeWidth={0}
                      label={({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
                        const RADIAN = Math.PI / 180;
                        const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
                        const x = cx + radius * Math.cos(-midAngle * RADIAN);
                        const y = cy + radius * Math.sin(-midAngle * RADIAN);
                        return percent > 0.08 ? (
                          <text x={x} y={y} fill="#fff" textAnchor="middle" dominantBaseline="central" fontSize={11} fontWeight={600}>
                            {(percent * 100).toFixed(0)}%
                          </text>
                        ) : null;
                      }}
                      labelLine={false}
                    >
                      {data.top_diagnoses.map((_, i) => <Cell key={i} fill={DIAG_COLORS[i]} />)}
                    </Pie>
                    <Tooltip contentStyle={tooltipStyle} formatter={(v) => [`${v} cazuri (${diagTotal ? ((v / diagTotal) * 100).toFixed(1) : 0}%)`, 'Numar']} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="w-1/2 space-y-2.5">
                {data.top_diagnoses.map((d, i) => {
                  const pct = diagTotal ? ((d.count / diagTotal) * 100).toFixed(1) : 0;
                  return (
                    <div key={i} className="flex items-start gap-2">
                      <span className="w-2.5 h-2.5 rounded-full mt-1 shrink-0" style={{ backgroundColor: DIAG_COLORS[i] }} />
                      <div className="min-w-0 flex-1">
                        <p className="text-xs text-slate-600 leading-tight truncate" title={d.name}>{d.name}</p>
                        <p className="text-xs font-bold text-slate-800">{d.count} <span className="font-normal text-slate-400">({pct}%)</span></p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </SectionCard>

        {/* HbA1c Donut with percentages */}
        <SectionCard title="Distributia HbA1c" subtitle="Ultimul rezultat per pacient">
          {hba1cTotal === 0 ? (
            <EmptyState text="Nu exista date HbA1c" />
          ) : (
            <div className="h-64">
              <div className="relative">
                <ResponsiveContainer width="100%" height={210}>
                  <PieChart>
                    <Pie
                      data={data.hba1c_distribution}
                      cx="50%" cy="50%"
                      innerRadius="55%" outerRadius="80%"
                      paddingAngle={3}
                      dataKey="value"
                      strokeWidth={0}
                      label={({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
                        if (percent === 0) return null;
                        const RADIAN = Math.PI / 180;
                        const radius = outerRadius + 16;
                        const x = cx + radius * Math.cos(-midAngle * RADIAN);
                        const y = cy + radius * Math.sin(-midAngle * RADIAN);
                        return (
                          <text x={x} y={y} fill="#475569" textAnchor={x > cx ? 'start' : 'end'} dominantBaseline="central" fontSize={11} fontWeight={600}>
                            {(percent * 100).toFixed(0)}%
                          </text>
                        );
                      }}
                      labelLine={{ stroke: '#CBD5E1', strokeWidth: 1 }}
                    >
                      {data.hba1c_distribution.map((e, i) => <Cell key={i} fill={e.color} />)}
                    </Pie>
                    <Tooltip contentStyle={tooltipStyle} formatter={(v, n) => [`${v} pacienti (${((v / hba1cTotal) * 100).toFixed(1)}%)`, n]} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none" style={{ height: 210 }}>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-slate-800">{hba1cTotal}</p>
                    <p className="text-[10px] text-slate-400">pacienti</p>
                  </div>
                </div>
              </div>
              <div className="flex justify-center gap-4 mt-1">
                {data.hba1c_distribution.map((h, i) => {
                  const pct = hba1cTotal ? ((h.value / hba1cTotal) * 100).toFixed(0) : 0;
                  return (
                    <div key={i} className="flex items-center gap-1.5 text-[11px] text-slate-600">
                      <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: h.color }} />
                      {h.name.split(' ')[0]} <span className="font-semibold">{h.value}</span> <span className="text-slate-400">({pct}%)</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </SectionCard>
      </div>

      {/* ── ROW 3: Demografie + Clase Medicamente (2 columns) ── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Demographics - Bar */}
        <SectionCard title="Demografia Pacientilor" subtitle="Distributie pe grupe de varsta">
          {data.demographics.every(d => d.count === 0) ? (
            <EmptyState text="Nu exista date demografice" />
          ) : (
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data.demographics} margin={{ top: 10, right: 20, left: 0, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                  <XAxis dataKey="group" stroke="#94A3B8" fontSize={12} tickLine={false} axisLine={false} />
                  <YAxis stroke="#94A3B8" fontSize={11} tickLine={false} axisLine={false} width={30} allowDecimals={false} />
                  <Tooltip contentStyle={tooltipStyle} formatter={(v) => [`${v} pacienti`, 'Numar']} />
                  <Bar dataKey="count" radius={[8, 8, 0, 0]} maxBarSize={60}>
                    {data.demographics.map((_, i) => <Cell key={i} fill={DEMO_COLORS[i]} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </SectionCard>

        {/* Medication Classes - Horizontal Bar */}
        <SectionCard title="Clase de Medicamente" subtitle="Numar pacienti per clasa">
          {data.medication_classes.every(m => m.count === 0) ? (
            <EmptyState text="Nu exista date despre medicamente" />
          ) : (
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data.medication_classes} layout="vertical" margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#F1F5F9" />
                  <XAxis type="number" tick={{ fontSize: 11 }} stroke="#94A3B8" tickLine={false} axisLine={false} />
                  <YAxis
                    type="category" dataKey="name" width={150}
                    tick={{ fontSize: 11 }} stroke="#94A3B8" tickLine={false} axisLine={false}
                  />
                  <Tooltip contentStyle={tooltipStyle} formatter={(v) => [`${v} pacienti`, 'Utilizare']} />
                  <Bar dataKey="count" radius={[0, 6, 6, 0]} maxBarSize={22}>
                    {data.medication_classes.map((_, i) => <Cell key={i} fill={MED_COLORS[i]} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </SectionCard>
      </div>

      {/* ── Today's Schedule ── */}
      <SectionCard title="Programul de Azi" subtitle={format(new Date(), "EEEE, d MMMM", { locale: ro })}>
        {data.today_schedule.length === 0 ? (
          <EmptyState text="Nu exista programari pentru azi" />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="today-schedule-table">
              <thead>
                <tr className="border-b border-slate-100">
                  <th className="text-left py-2.5 px-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Ora</th>
                  <th className="text-left py-2.5 px-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Nume Pacient</th>
                  <th className="text-left py-2.5 px-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Tip</th>
                  <th className="text-left py-2.5 px-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Status</th>
                </tr>
              </thead>
              <tbody>
                {data.today_schedule.map((row, i) => {
                  const st = STATUS_MAP[row.status] || STATUS_MAP.scheduled;
                  return (
                    <tr key={i} className="border-b border-slate-50 hover:bg-slate-50/50 transition-colors" data-testid={`schedule-row-${i}`}>
                      <td className="py-2.5 px-3 font-mono font-semibold text-slate-800">{row.time}</td>
                      <td className="py-2.5 px-3 text-slate-700">{row.patient_name}</td>
                      <td className="py-2.5 px-3">
                        <Badge variant="outline" className={cn('text-[11px]', row.type === 'Control' ? 'border-blue-200 text-blue-700' : 'border-emerald-200 text-emerald-700')}>
                          {row.type === 'Initiala' ? 'Initiala' : 'Control'}
                        </Badge>
                      </td>
                      <td className="py-2.5 px-3"><Badge className={cn('text-[11px]', st.cls)}>{st.label}</Badge></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </SectionCard>
    </div>
  );
};

const EmptyState = ({ text }) => (
  <div className="flex items-center justify-center h-full min-h-[120px] text-slate-400 text-sm">{text}</div>
);

export default ReportsPage;
